import { generatePath, useNavigate } from 'react-router-dom';

import { CancelSaveButtons } from '@components/cancel-save-buttons';
import { createForm, useCreateFormStore } from '@components/form';
import { MaxLengthCounter } from '@components/max-length-counter';
import { hideModal, Modal, useModal } from '@components/modal';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { useScopedTranslate } from '@locale/translate';

import { useCurrentCompany } from 'api/authentication/hooks';
import { useCreateJourney } from 'api/journeys/hooks';
import { useWorkspaces } from 'api/workspaces/hooks';
import { Workspace } from 'api/workspaces/requests';
import { useCurrentWorkspace } from 'ui/authenticated/main/helpers';
import { routePaths } from 'ui/authenticated/navigation';

const MAX_TITLE_LENGTH = 50;

type CreateJourneyFormData = {
  title: string;
  workspace: string;
};

type CreateJourneyModalProps = {
  workspaceId?: string;
};

const Form = createForm<CreateJourneyFormData>({ space: 0 });

export const useCreateJourneyModal = () => useModal(CreateJourneyModal);

// Every candidate is checked against the workspaces the user actually has: the
// onboarding config carries ids that can be stale, or from another company.
export const resolveWorkspaceId = ({
  candidateIds,
  workspaces,
}: {
  candidateIds: (string | undefined)[];
  workspaces: Workspace[];
}) => {
  const candidateId = candidateIds.find((id) => {
    return Boolean(id) && workspaces.some((workspace) => workspace.shortId === id);
  });

  return candidateId ?? (workspaces.length === 1 ? workspaces[0].shortId : undefined);
};

export const CreateJourneyModal = ({ workspaceId }: CreateJourneyModalProps) => {
  const { t } = useScopedTranslate(locale);
  const formStore = useCreateFormStore();
  const navigate = useNavigate();
  const createMutation = useCreateJourney();
  const { data: workspaces, isLoading: isLoadingWorkspaces } = useWorkspaces();
  const { workspaceId: viewedWorkspaceId } = useCurrentWorkspace();
  const company = useCurrentCompany();

  const title = (formStore.useFieldValue('title') as string) ?? '';
  const trimmedTitle = title.trim();
  const pickedWorkspaceId = (formStore.useFieldValue('workspace') as string) ?? '';

  const resolvedWorkspaceId = resolveWorkspaceId({
    candidateIds: [
      workspaceId,
      viewedWorkspaceId,
      company?.onboarding?.status.config.homeWorkspaceId,
    ],
    workspaces: workspaces ?? [],
  });
  const targetWorkspaceId = resolvedWorkspaceId || pickedWorkspaceId;
  const hasWorkspaces = Boolean(workspaces?.length);
  const mustPickWorkspace = !isLoadingWorkspaces && !resolvedWorkspaceId && hasWorkspaces;
  const hasNoWorkspaces = !isLoadingWorkspaces && !hasWorkspaces;

  const handleSubmit = () => {
    if (!trimmedTitle || !targetWorkspaceId) {
      return;
    }

    createMutation.mutate(
      { title: trimmedTitle, workspace: targetWorkspaceId },
      {
        onSuccess: (data) => {
          hideModal();
          navigate(
            generatePath(routePaths.editJourney, {
              workspaceId: targetWorkspaceId,
              journeyId: data.shortId,
              '*': '',
            })
          );
        },
      }
    );
  };

  return (
    <Modal isLoading={createMutation.isLoading || isLoadingWorkspaces}>
      <Form store={formStore} onSubmit={handleSubmit}>
        <Modal.SimpleHeader title={t('title')} />
        <Modal.Content>
          <Stack space={4}>
            <Stack space={1}>
              <Form.TextField
                id="title"
                label={t('journeyNameLabel')}
                placeholder={t('journeyNamePlaceholder')}
                autoFocus
                autoComplete="off"
                maxChars={MAX_TITLE_LENGTH}
              />
              <MaxLengthCounter currentLength={title.length} maxLength={MAX_TITLE_LENGTH} />
            </Stack>
            {mustPickWorkspace && (
              <Form.Select
                id="workspace"
                label={t('workspaceLabel')}
                placeholder={t('workspacePlaceholder')}
                options={(workspaces ?? []).map((workspace) => ({
                  value: workspace.shortId,
                  label: workspace.title,
                }))}
              />
            )}
            {hasNoWorkspaces && (
              <Text font="regular14" color="textSecondary" text={t('noWorkspaces')} />
            )}
          </Stack>
        </Modal.Content>
        <Modal.Footer>
          <CancelSaveButtons
            alwaysShow
            onCancel={hideModal}
            onSave={handleSubmit}
            translationsOverride={{ save: t('createJourney') }}
            isDisabled={createMutation.isLoading}
            isSaveDisabled={!trimmedTitle || !targetWorkspaceId}
          />
        </Modal.Footer>
      </Form>
    </Modal>
  );
};

const locale = {
  en: {
    title: 'Create journey',
    journeyNameLabel: 'Journey name',
    journeyNamePlaceholder: 'Enter journey name',
    workspaceLabel: 'Workspace',
    workspacePlaceholder: 'Select a workspace',
    noWorkspaces: 'Create a workspace before you create a journey',
    createJourney: 'Create journey',
  },
};
